import { lazy, type ComponentType, type FC } from 'react';
import { createBrowserRouter, RouteObject, useRoutes } from 'react-router';

import LoginPage from '@/pages/LoginPage';
import PersonalDataPage from '@/pages/PersonalDataPage';
import ExternalServicesPage from '@/pages/Services/ExternalServicesPage';
import HomePage from '@/pages/Services/HomePage';
import InternalServicesPage from '@/pages/Services/InternalServicesPage';
import Profile from '@/pages/UserAccount/Profile';
import UserRequests from '@/pages/UserAccount/UserRequests';

import { MainTemplate } from '@/widgets/Templates';

import ErrorBoundary from '@/components/ErrorBoundary';
import { PrivateRouteWrapper } from '@/components/PrivateRouteWrapper';
import { GercRedirectMiddleware } from '@/middlewares/GercRedirectMiddleware';

import { MspAccessGuard } from '@/modules/msp/components/MspAccessGuard';

import { ARSRoutes } from '@/modules/ars/routes';
import { BiaRoutes } from '@/modules/bia/routes';
import { ErzLegacyRoutes, ErzRoutes } from '@/modules/erz/routes';
import { IacLegacyRoutes, IACRouter } from '@/modules/iac/routes';
import { PayRouter } from '@/modules/pay/routes';
import { RmpRouter } from '@/modules/rmp/routes';
import { RmpcRouter } from '@/modules/rmpc/routes';
import { WNTRoutes } from '@/modules/wnt/routes';

import { ROUTES } from './config';
import { buildRouteInfoMap, wrapRoutesWithSeo } from './utils/SeoUtils';

const LazyMspRouter = lazy(() =>
  import('msp/MspRouter').then(({ MspRouter }) => {
    const Wrapper: FC = () => useRoutes([{ ...MspRouter, path: undefined }]) ?? null;
    return { default: Wrapper };
  }),
);

const lazyDefaultComponent =
  (loadComponent: () => Promise<{ default: ComponentType }>): RouteObject['lazy'] =>
  async () => {
    const { default: Component } = await loadComponent();
    return { Component };
  };

const PublicRoutes: RouteObject[] = [
  { path: ROUTES.EXTERNAL_SERVICES, element: <ExternalServicesPage /> },
  { path: ROUTES.PERSONAL_DATA, element: <PersonalDataPage /> },
  { path: ROUTES.GERC_REDIRECT, element: <GercRedirectMiddleware /> },
  {
    index: true,
    path: ROUTES.HOME,
    element: <HomePage />,
  },
  {
    path: ROUTES.INTERNAL_SERVICES,
    element: <InternalServicesPage />,
  },
  { path: ROUTES.MAP, lazy: lazyDefaultComponent(() => import('@/pages/Services/HomePage/MapsPage')) },
];

const PrivateRoutes: RouteObject[] = [
  {
    Component: PrivateRouteWrapper,
    children: [
      { path: ROUTES.USER.PROFILE, element: <Profile /> },
      { path: ROUTES.USER.REQUESTS.ACTIVE_REQUESTS, element: <UserRequests tab="active" /> },
      { path: ROUTES.USER.REQUESTS.ARCHIVED_REQUESTS, element: <UserRequests tab="archive" /> },
      { path: ROUTES.USER.REQUESTS.DRAFT_REQUESTS, element: <UserRequests tab="draft" /> },
      {
        path: 'msp/*',
        element: (
          <MspAccessGuard>
            <LazyMspRouter />
          </MspAccessGuard>
        ),
      },
    ],
  },
];

const MainRoutes: RouteObject[] = [...PublicRoutes, ...PrivateRoutes];
const legacyRoutes = [...ErzLegacyRoutes, ...IacLegacyRoutes];

export const routes: RouteObject[] = [
  {
    path: ROUTES.HOME,
    ErrorBoundary: ErrorBoundary,
    children: [
      {
        children: [
          {
            Component: MainTemplate,
            children: [
              ...MainRoutes,
              ARSRoutes,
              BiaRoutes,
              ErzRoutes,
              IACRouter,
              PayRouter,
              RmpRouter,
              RmpcRouter,
              WNTRoutes,
            ],
          },
        ],
      },
      { path: ROUTES.LOGIN, element: <LoginPage /> },
      ...legacyRoutes,
    ],
  },
];

const routeInfoMap = buildRouteInfoMap();

const wrappedRoutes = wrapRoutesWithSeo(routes, routeInfoMap);

export const router = createBrowserRouter(wrappedRoutes);
